import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Typography, Box, makeStyles, Paper } from '@material-ui/core';
import CardSectionTitle from '@components/CardSection/CardSectionTitle/CardSectionTitle';
import AppointmentDetails from '@components/RequestForm/AppointmentDetails/AppointmentDetails';
import CardSection from '@components/CardSection/CardSection';
import DocumentRequest from '@components/RequestForm/DocumentRequest/DocumentRequest';
import FullPatientNameInput from '@components/RequestForm/PatientDetails/PatientName/FullPatientNameInput';
import useIsDocRequired from '@components/RequestForm/utilities/useIsDocRequired';
import DiagnosisInput from '@components/RequestForm/inputs/DiagnosisInput';
import PreCertNumberInput from '@components/RequestForm/inputs/PreCertNumberInput';
import CodeChipInput from '@components/ProcedureForm/ProcedureFormFields/CodeChipInput';
import { ProcedureNameInput } from '@components/RequestForm/inputs/ProcedureName/ProcedureNameInput/ProcedureNameInput';
import { CustomProcedureNameValidation } from '@components/RequestForm/RequestFormValidation';
import { SurgeonDropdown } from '@components/RequestForm/inputs/SurgeonDropdown/SurgeonDropdown';
import { useSsmUiFlags } from '@utilities/hooks/useSsmUiFlags/useSsmUiFlags';
import { SecondarySurgeonSelect } from '@components/ProcedureForm/inputs/SecondarySurgeonSelect/SecondarySurgeonSelect';
import { StatefulTextInput } from '@components/StatefulInputs/StatefulTextInput/StatefulTextInput';
import { SECONDARY_SURGEON_NAME_REQUIRED } from '@utilities/Validation/ValidationMessages';
import DocumentRequestFormType from '@interfaces/RequestForm/forms/DocumentRequestFormType';

const useStyles = makeStyles(theme => ({
	documentTitle: {
		display: 'flex',
		flexDirection: 'row',
		gap: theme.spacing(1),
	},
	procedureName: {
		'display': 'flex',
		'marginBottom': theme.spacing(4),
		'width': '50%',
		[theme.breakpoints.down('sm')]: {
			width: '100%',
		},
		'& .MuiInputLabel-outlined': {
			color: theme.palette.common.black,
			fontSize: theme.typography.caption.fontSize,
		},
		'& > *': {
			flex: 1,
		},
		'& .MuiFormHelperText-root.Mui-error': {
			'fontWeight': theme.typography.fontWeightBold,
			'fontSize': theme.typography.caption.fontSize,
			'& > .MuiSvgIcon-root': {
				height: '18px',
				width: '18px',
			},
		},
	},
	surgeonDropdown: {
		'display': 'flex',
		'marginBottom': theme.spacing(4),
		'marginTop': theme.spacing(4),
		'width': '100%',

		'& > *': {
			flex: 1,
		},
	},
	secondarySurgeonSelect: {
		marginBottom: theme.spacing(1.5),
		marginTop: theme.spacing(4),
	},
	customSecondarySurgeon: {
		backgroundColor: theme.palette.primary[50],
		border: `1px solid ${theme.palette.primary.main}`,
		padding: theme.spacing(3),
		borderRadius: '4px',
	},
	customSecondarySurgeonInput: {
		'width': '100%',
		'& > div': {
			backgroundColor: theme.palette.common.white,
		},
		'& .MuiFormLabel-root.MuiInputLabel-root': {
			backgroundColor: theme.palette.primary[50],
		},
		'& .MuiFormHelperText-root.Mui-error': {
			backgroundColor: theme.palette.primary[50],
		}
	},
}));

const DocumentsRequiredTitle: React.FC = () => {
	const classes = useStyles();
	const isDocRequired = useIsDocRequired();

	return <Box className={classes.documentTitle}>
		<Typography variant="h5" component="h2">Documents</Typography>
		{isDocRequired && <Typography variant="h5" color="error" component="span"> *</Typography>}
	</Box>;
};

const ComposedDocumentSections = () => {
	const classes = useStyles();
	const { wShowSecondarySurgeon } = useSsmUiFlags();
	const {
		watch,
		setValue
	} = useFormContext<DocumentRequestFormType>();

	const currentValues = watch();

	const handleSecondarySurgeonCustomChange = React.useCallback((event: React.ChangeEvent<{ value: string | null }>) => {
		if (currentValues.secondarySurgeon?.isCustom && event.target.value) {
			setValue('customSecondarySurgeon', event.target.value);
			return;
		}
	}, [currentValues.secondarySurgeon?.isCustom, setValue]);

	return <>
		<CardSection sectionTitle={<CardSectionTitle title="Appointment Details" />} >
			<AppointmentDetails />
			<Box className={classes.surgeonDropdown}>
				<SurgeonDropdown dataField="requestForm-appointmentDetailsDocument-surgeonSelect" placeholder="Select an option" />
			</Box>
			{wShowSecondarySurgeon && (
				<Box className={classes.secondarySurgeonSelect}>
					<SecondarySurgeonSelect dataField="documentRequestForm-secondarySurgeonSelect" />
					{currentValues.secondarySurgeon?.isCustom && (
						<Paper
							elevation={0}
							data-field="documentRequestForm-customSecondarySurgeon"
							className={classes.customSecondarySurgeon}
						>
							<StatefulTextInput
								name="customSecondarySurgeon"
								label="Custom secondary surgeon name"
								dataField="documentRequestForm-customSecondarySurgeon-textInput"
								styleAsRequired={true}
								wrapperClassName={classes.customSecondarySurgeonInput}
								controllerOptions={{
									rules: {
										required: SECONDARY_SURGEON_NAME_REQUIRED,
										onChange: handleSecondarySurgeonCustomChange,
									},
								}}
							/>
						</Paper>
					)}
				</Box>
			)}
			<DiagnosisInput type="primary" />
			<CodeChipInput />
			<PreCertNumberInput type="Primary" />
			<Box className={classes.procedureName}>
				<ProcedureNameInput
					label="PROCEDURE"
					name="procedureName"
					controllerOptions={{
						rules: CustomProcedureNameValidation,
					}}
					dataField="requestForm-appointmentDetailsDocument-procedureNameTextInput"
				/>
			</Box>
			<FullPatientNameInput />
		</CardSection>
		<CardSection sectionTitle={<CardSectionTitle title={<DocumentsRequiredTitle />} />}>
			<DocumentRequest />
		</CardSection>
	</>;
};

export default ComposedDocumentSections;
